// Settings → Integrations tab.
//
// Source-of-truth reference: /var/www/kitchenos/portal-standalone.html
//   tab strip:                line 2751 (button data-tab="integrations")
//   panel container:          line 2760 (#settings-panel-integrations)
//   INTEGRATIONS_CATALOG:     line 9112 (Xero / QuickBooks / Sage / Stripe)
//   renderIntegrationsPanel:  line 9132
//   social card renderer:     line 9181 (Instagram / Facebook / LinkedIn / TikTok)
//   kosIntConnect / Disconnect: line 9268, 9305
//   kosSocialConnect (handle modal): line 9219
//
// Master-doc spec (TKOS_MASTER_CONTEXT.md:489) says:
//   Accounting (0/3 connected): Xero, QuickBooks Online, Sage Business Cloud
//   Social Media (0/4): Instagram, Facebook, LinkedIn, + other
// We follow the master-doc spec (3 accounting + 4 social) since this is the
// rebuild; the source-of-truth's Stripe card belongs to the Payments tab.
//
// Backend wiring
// --------------
// kitchenos-api exposes `/api/retailer/:retailerId/integrations` (server.js:568,
// modules/integrations.js) but the retailerId is not yet plumbed through the
// portal shell. There is no portal-relative `/api/portal/integrations` route.
//
// We therefore:
//   1. Try `GET /portal/integrations` first.
//   2. On 404 / 501 / 401, render the catalogue with "Not connected" status
//      for everything.
//   3. Each "Connect" button toasts "Coming soon — OAuth integration pending"
//      (with TODO comments) — actual OAuth is out of scope for the rebuild.

import { ApiError } from "@tkos/api-client";
import { confirmDialog, iconMarkup } from "@tkos/shared-ui";

import type { PageContext } from "../../../shell/types";
import type { SettingsTab, TabCleanup } from "../types";

import "./integrations.css";

// ── Provider catalogues (static, master-doc compliant) ───────────────────────

interface AccountingProvider {
  id: "xero" | "quickbooks" | "sage";
  name: string;
  tag: string;
  /** Logo background colour. */
  logoBg: string;
  /** 1–3 char glyph rendered inside the logo tile. */
  logoText: string;
  desc: string;
  features: readonly string[];
  /** Setup-guide steps shown on Connect click — verbatim from source-of-truth
   *  /var/www/kitchenos/portal-standalone.html INTEGRATIONS_CATALOG (line 9112). */
  setup: readonly string[];
}

interface SocialProvider {
  id: "instagram" | "facebook" | "linkedin" | "other";
  name: string;
  /** Emoji used as logo glyph. */
  emoji: string;
  color: string;
  desc: string;
}

const ACCOUNTING_PROVIDERS: readonly AccountingProvider[] = [
  {
    id: "xero",
    name: "Xero",
    tag: "Cloud accounting",
    logoBg: "#13B5EA",
    logoText: "X",
    desc: "Two-way sync between The Kitchen OS and your Xero organisation. Quotes, invoices, payments and customer contacts stay in lock-step — no double entry.",
    features: ["Invoices", "Payments", "Customers", "VAT (MTD)", "Bank rec"],
    setup: [
      "Sign in to Xero with the owner account",
      "Authorise The Kitchen OS to access your organisation",
      "Map your Sales / VAT accounts",
      "Choose which jobs trigger invoices",
    ],
  },
  {
    id: "quickbooks",
    name: "QuickBooks Online",
    tag: "Cloud accounting",
    logoBg: "#2CA01C",
    logoText: "qb",
    desc: "Push approved quotes straight into QuickBooks Online as estimates and invoices, with deposits and final balances tracked back into the portal.",
    features: ["Estimates", "Invoices", "Payments", "Customers", "VAT"],
    setup: [
      "Connect with your QuickBooks Online account",
      "Pick the company file to sync",
      "Map deposit / final invoice items",
      "Enable real-time payment webhooks",
    ],
  },
  {
    id: "sage",
    name: "Sage Business Cloud",
    tag: "UK accounting",
    logoBg: "#00DC00",
    logoText: "§",
    desc: "Popular with UK retailers — sync invoices, customer accounts and supplier bills into Sage Business Cloud Accounting and keep VAT MTD-ready.",
    features: ["Sales invoices", "Purchase invoices", "Customers", "VAT MTD"],
    setup: [
      "Authorise Sage Business Cloud Accounting",
      "Select the business to connect",
      "Map nominal codes for kitchen sales",
      "Enable supplier invoice import",
    ],
  },
];

const SOCIAL_PROVIDERS: readonly SocialProvider[] = [
  {
    id: "instagram",
    name: "Instagram",
    emoji: "\u{1F4F8}",
    color: "#e1306c",
    desc: "Share kitchen photos and reels. AI-generated captions posted directly from the Social Media AI widget.",
  },
  {
    id: "facebook",
    name: "Facebook",
    emoji: "\u{1F44D}",
    color: "#1877f2",
    desc: "Post to your business page and reach local homeowners with kitchen showcases and promotions.",
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    emoji: "\u{1F4BC}",
    color: "#0a66c2",
    desc: "Build your professional brand with industry insights, project showcases and trade content.",
  },
  {
    id: "other",
    name: "Other platform",
    emoji: "\u{2795}",
    color: "#64748b",
    desc: "Request another platform — we'll add it when there's demand. TikTok, YouTube, Pinterest all on the roadmap.",
  },
];

// ── Connection state ─────────────────────────────────────────────────────────

interface ConnectionState {
  connected: boolean;
  /** Account name / handle / organisation, when connected. */
  detail?: string;
  /** Unix-ms since connection. */
  since?: number;
}

type AccountingState = Record<AccountingProvider["id"], ConnectionState>;
type SocialState = Record<SocialProvider["id"], ConnectionState>;

interface IntegrationsResponse {
  ok?: boolean;
  integrations?: Record<string, { connected?: boolean; org?: string; since?: number }>;
  social?: Record<string, { connected?: boolean; handle?: string; since?: number }>;
}

function emptyAccountingState(): AccountingState {
  return {
    xero: { connected: false },
    quickbooks: { connected: false },
    sage: { connected: false },
  };
}

function emptySocialState(): SocialState {
  return {
    instagram: { connected: false },
    facebook: { connected: false },
    linkedin: { connected: false },
    other: { connected: false },
  };
}

// ── Utilities ────────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Backend ──────────────────────────────────────────────────────────────────

async function loadState(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<{
  accounting: AccountingState;
  social: SocialState;
  usedFallback: boolean;
}> {
  try {
    // TODO: GET /api/portal/integrations — portal-scoped endpoint pending.
    // The retailer-scoped endpoint at /api/retailer/:retailerId/integrations
    // is server-ready but needs the shell to thread retailerId through ctx.
    const resp = await ctx.api<IntegrationsResponse>("/portal/integrations", {
      signal,
    });

    const accounting = emptyAccountingState();
    const intMap = resp?.integrations ?? {};
    for (const id of ["xero", "quickbooks", "sage"] as const) {
      const raw = intMap[id];
      if (raw && raw.connected) {
        const next: ConnectionState = { connected: true };
        if (typeof raw.org === "string") next.detail = raw.org;
        if (typeof raw.since === "number") next.since = raw.since;
        accounting[id] = next;
      }
    }

    const social = emptySocialState();
    const socMap = resp?.social ?? {};
    for (const id of ["instagram", "facebook", "linkedin", "other"] as const) {
      const raw = socMap[id];
      if (raw && raw.connected) {
        const next: ConnectionState = { connected: true };
        if (typeof raw.handle === "string") next.detail = raw.handle;
        if (typeof raw.since === "number") next.since = raw.since;
        social[id] = next;
      }
    }

    return { accounting, social, usedFallback: false };
  } catch (err) {
    if (signal.aborted || isAbortError(err)) throw err;
    if (
      err instanceof ApiError &&
      (err.status === 404 || err.status === 501 || err.status === 401)
    ) {
      return {
        accounting: emptyAccountingState(),
        social: emptySocialState(),
        usedFallback: true,
      };
    }
    console.warn("[settings/integrations] state fetch failed:", err);
    return {
      accounting: emptyAccountingState(),
      social: emptySocialState(),
      usedFallback: true,
    };
  }
}

// ── Rendering ────────────────────────────────────────────────────────────────

function formatSince(ms: number | undefined): string {
  if (!ms || !Number.isFinite(ms)) return "Setup takes about 2 minutes";
  try {
    return `Connected ${new Date(ms).toLocaleDateString("en-GB")}`;
  } catch {
    return "Setup takes about 2 minutes";
  }
}

function renderShell(container: HTMLElement): {
  bannerEl: HTMLElement;
  acctCountEl: HTMLElement;
  acctGridEl: HTMLElement;
  socialCountEl: HTMLElement;
  socialGridEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="set-int-root" aria-label="Integrations">
      <div class="set-int-banner" data-role="banner" hidden></div>

      <section class="set-int-section">
        <header class="set-int-section-head">
          <div>
            <h2 class="set-section-title">Accounting</h2>
            <p class="set-help">Plug your accounting software into The Kitchen OS so invoices, payments and customers stay in sync — no copy-paste, no missed reconciliations.</p>
          </div>
          <span class="set-int-count" data-role="acct-count">0 of ${ACCOUNTING_PROVIDERS.length} connected</span>
        </header>
        <div class="set-int-grid" data-role="acct-grid"></div>
      </section>

      <section class="set-int-section">
        <header class="set-int-section-head">
          <div>
            <h2 class="set-section-title">Social Media</h2>
            <p class="set-help">Connect your social accounts to publish AI-generated posts directly from the Social Media AI widget — no switching between apps.</p>
          </div>
          <span class="set-int-count" data-role="social-count">0 of ${SOCIAL_PROVIDERS.length} connected</span>
        </header>
        <div class="set-int-grid" data-role="social-grid"></div>
      </section>
    </section>
  `;

  return {
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    acctCountEl: container.querySelector<HTMLElement>("[data-role='acct-count']")!,
    acctGridEl: container.querySelector<HTMLElement>("[data-role='acct-grid']")!,
    socialCountEl: container.querySelector<HTMLElement>("[data-role='social-count']")!,
    socialGridEl: container.querySelector<HTMLElement>("[data-role='social-grid']")!,
  };
}

function renderAccountingCards(
  host: HTMLElement,
  state: AccountingState,
): void {
  host.innerHTML = ACCOUNTING_PROVIDERS.map((p) => {
    const s = state[p.id];
    const dotClass = s.connected
      ? "set-int-dot set-int-dot--on"
      : "set-int-dot set-int-dot--off";
    const statusText = s.connected
      ? `Connected${s.detail ? ` · ${escapeHtml(s.detail)}` : ""}`
      : "Not connected";
    const btn = s.connected
      ? `<button type="button" class="set-int-btn set-int-btn--ghost" data-kind="accounting" data-action="disconnect" data-id="${p.id}">Disconnect</button>`
      : `<button type="button" class="set-int-btn" data-kind="accounting" data-action="connect" data-id="${p.id}">Connect</button>`;
    return `
      <article class="set-int-card${s.connected ? " set-int-card--connected" : ""}" data-id="${p.id}">
        <header class="set-int-card-head">
          <div class="set-int-logo" style="background:${p.logoBg}">${escapeHtml(p.logoText)}</div>
          <div class="set-int-card-titles">
            <div class="set-int-name">${escapeHtml(p.name)}</div>
            <div class="set-int-tag">${escapeHtml(p.tag)}</div>
          </div>
          <div class="set-int-status">
            <span class="${dotClass}" aria-hidden="true"></span>
            <span class="set-int-status-text">${statusText}</span>
          </div>
        </header>
        <p class="set-int-desc">${escapeHtml(p.desc)}</p>
        <div class="set-int-features">
          ${p.features.map((f) => `<span class="set-int-feature">${escapeHtml(f)}</span>`).join("")}
        </div>
        <footer class="set-int-card-foot">
          <span class="set-int-foot-hint">${escapeHtml(formatSince(s.since))}</span>
          ${btn}
        </footer>
      </article>
    `;
  }).join("");
}

function renderSocialCards(host: HTMLElement, state: SocialState): void {
  host.innerHTML = SOCIAL_PROVIDERS.map((p) => {
    const s = state[p.id];
    const dotClass = s.connected
      ? "set-int-dot set-int-dot--on"
      : "set-int-dot set-int-dot--off";
    const statusText = s.connected
      ? `Connected${s.detail ? ` · @${escapeHtml(s.detail)}` : ""}`
      : "Not connected";
    const btn = s.connected
      ? `<button type="button" class="set-int-btn set-int-btn--ghost" data-kind="social" data-action="disconnect" data-id="${p.id}">Disconnect</button>`
      : `<button type="button" class="set-int-btn" data-kind="social" data-action="connect" data-id="${p.id}">Connect ${escapeHtml(p.name)}</button>`;
    return `
      <article class="set-int-card${s.connected ? " set-int-card--connected" : ""}" data-id="${p.id}">
        <header class="set-int-card-head">
          <div class="set-int-logo set-int-logo--emoji" style="background:${p.color}">
            <span aria-hidden="true">${p.emoji}</span>
          </div>
          <div class="set-int-card-titles">
            <div class="set-int-name">${escapeHtml(p.name)}</div>
            <div class="set-int-tag">Social media</div>
          </div>
          <div class="set-int-status">
            <span class="${dotClass}" aria-hidden="true"></span>
            <span class="set-int-status-text">${statusText}</span>
          </div>
        </header>
        <p class="set-int-desc">${escapeHtml(p.desc)}</p>
        <div class="set-int-features">
          <span class="set-int-feature">AI captions</span>
          <span class="set-int-feature">Direct publish</span>
          <span class="set-int-feature">Schedule posts</span>
        </div>
        <footer class="set-int-card-foot">
          <span class="set-int-foot-hint">${escapeHtml(formatSince(s.since))}</span>
          ${btn}
        </footer>
      </article>
    `;
  }).join("");
}

function countConnected<T extends ConnectionState>(state: Record<string, T>): number {
  return Object.values(state).filter((s) => s.connected).length;
}

// ── Setup-guide modal (verbatim port of source kosIntConnect, line 9268) ────
//
// Shows the numbered setup steps + demo-connection org input when the retailer
// clicks Connect on Xero / QBO / Sage. The Authorise button toasts "OAuth flow
// coming soon" — real OAuth still routes through the retailer-scoped endpoint
// in modules/integrations.js once the shell plumbs retailerId through ctx.

function openAccountingSetupGuide(
  provider: AccountingProvider,
  ctx: PageContext,
): void {
  const existing = document.getElementById("set-int-setup-overlay");
  if (existing) existing.remove();

  const overlay = document.createElement("div");
  overlay.id = "set-int-setup-overlay";
  overlay.className = "set-int-setup-overlay";
  const stepsHtml = provider.setup
    .map(
      (s, i) =>
        '<li class="set-int-setup-step">' +
          '<span class="set-int-setup-num">' + (i + 1) + '</span>' +
          '<span>' + escapeHtml(s) + '</span>' +
        '</li>',
    )
    .join("");

  overlay.innerHTML =
    '<div class="set-int-setup-card" role="dialog" aria-modal="true" aria-labelledby="set-int-setup-title">' +
      '<header class="set-int-setup-head">' +
        '<div class="set-int-logo" style="background:' + provider.logoBg + ';width:42px;height:42px">' +
          escapeHtml(provider.logoText) +
        '</div>' +
        '<div class="set-int-setup-titles">' +
          '<h3 id="set-int-setup-title">Connect to ' + escapeHtml(provider.name) + '</h3>' +
          '<div class="set-int-setup-tag">' + escapeHtml(provider.tag) + '</div>' +
        '</div>' +
        '<button type="button" class="set-int-setup-x" data-role="close" aria-label="Close">×</button>' +
      '</header>' +
      '<p class="set-int-setup-desc">' + escapeHtml(provider.desc) + '</p>' +
      '<div class="set-int-setup-label">Setup steps</div>' +
      '<ol class="set-int-setup-list">' + stepsHtml + '</ol>' +
      '<div class="set-int-setup-org">' +
        '<div class="set-int-setup-label">Demo connection</div>' +
        '<label class="set-int-setup-field">' +
          '<span>Organisation / company name</span>' +
          '<input type="text" data-role="org" placeholder="e.g. Ball Kitchens Ltd" autocomplete="off">' +
        '</label>' +
      '</div>' +
      '<div class="set-int-setup-actions">' +
        '<button type="button" class="set-int-btn set-int-btn--ghost" data-role="cancel">Cancel</button>' +
        '<button type="button" class="set-int-btn" data-role="authorise">Authorise &amp; connect</button>' +
      '</div>' +
    '</div>';

  const close = (): void => { overlay.remove(); };

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='authorise']")?.addEventListener("click", () => {
    // Real OAuth lands once retailerId is threaded through ctx. The setup
    // guide is the user-visible deliverable; the Authorise step closes
    // the modal with a "coming soon" toast for now.
    close();
    ctx.showToast(
      `${provider.name} OAuth flow coming soon — setup guide above shows the steps.`,
      "info",
    );
  });

  document.body.appendChild(overlay);
  setTimeout(() => {
    overlay.querySelector<HTMLInputElement>("[data-role='org']")?.focus();
  }, 60);
}

// ── Tab ──────────────────────────────────────────────────────────────────────

const tab: SettingsTab = {
  id: "integrations",
  label: "Integrations",
  icon: iconMarkup("link", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    const ac = new AbortController();
    let accounting: AccountingState = emptyAccountingState();
    let social: SocialState = emptySocialState();
    let disposed = false;

    const { bannerEl, acctCountEl, acctGridEl, socialCountEl, socialGridEl } =
      renderShell(container);

    function setBanner(msg: string | null): void {
      if (!msg) {
        bannerEl.hidden = true;
        bannerEl.textContent = "";
        return;
      }
      bannerEl.hidden = false;
      bannerEl.textContent = msg;
    }

    function rerender(): void {
      renderAccountingCards(acctGridEl, accounting);
      renderSocialCards(socialGridEl, social);
      acctCountEl.textContent = `${countConnected(accounting)} of ${ACCOUNTING_PROVIDERS.length} connected`;
      socialCountEl.textContent = `${countConnected(social)} of ${SOCIAL_PROVIDERS.length} connected`;
    }

    // ── Event delegation ──────────────────────────────────────────────────
    const onGridClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest<HTMLButtonElement>("[data-action]");
      if (!btn) return;
      const kind = btn.dataset.kind;
      const action = btn.dataset.action;
      const id = btn.dataset.id;
      if (!kind || !action || !id) return;

      if (action === "connect") {
        if (kind === "accounting") {
          const acctId = id as AccountingProvider["id"];
          const provider = ACCOUNTING_PROVIDERS.find((p) => p.id === acctId);
          if (provider) {
            openAccountingSetupGuide(provider, ctx);
            return;
          }
        }
        // Social Connect: still routes through the OAuth-pending toast until
        // the social platforms' own OAuth flows land.
        ctx.showToast("Coming soon — OAuth integration pending", "info");
      } else if (action === "disconnect") {
        // TODO: DELETE /api/retailer/:retailerId/integrations/:provider
        // (kitchenos-api/modules/integrations.js:450).
        void (async () => {
          if (kind === "accounting") {
            const acctId = id as AccountingProvider["id"];
            if (!(acctId in accounting)) return;
            if (!(await confirmDialog("Disconnect this integration?", {
              title: "Disconnect integration",
              confirmLabel: "Disconnect",
              destructive: true,
            }))) return;
            accounting = { ...accounting, [acctId]: { connected: false } };
          } else if (kind === "social") {
            const socId = id as SocialProvider["id"];
            if (!(socId in social)) return;
            if (!(await confirmDialog("Disconnect this account?", {
              title: "Disconnect account",
              confirmLabel: "Disconnect",
              destructive: true,
            }))) return;
            social = { ...social, [socId]: { connected: false } };
          }
          rerender();
          ctx.showToast("Disconnected", "info");
        })();
      }
    };
    acctGridEl.addEventListener("click", onGridClick);
    socialGridEl.addEventListener("click", onGridClick);

    // ── Data fan-out ──────────────────────────────────────────────────────
    async function load(): Promise<void> {
      try {
        const res = await loadState(ctx, ac.signal);
        if (disposed || ac.signal.aborted) return;
        accounting = res.accounting;
        social = res.social;
        if (res.usedFallback) {
          // Endpoint is live (GET /api/portal/integrations) but returned no
          // data for this retailer yet (no connections established). That's
          // a clean empty-catalogue state — no banner needed.
          setBanner(null);
        } else {
          setBanner(null);
        }
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load integrations (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "Couldn't load integrations";
        setBanner(`${msg} — showing empty catalogue.`);
        accounting = emptyAccountingState();
        social = emptySocialState();
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) rerender();
      }
    }

    // Initial paint immediately with empty state…
    rerender();
    void load();

    // ── Cleanup ───────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      ac.abort();
      acctGridEl.removeEventListener("click", onGridClick);
      socialGridEl.removeEventListener("click", onGridClick);
    };
  },
};

export default tab;
